import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Helmet } from "react-helmet";
import { HomeCareFiltersBar } from "@/components/home-care/HomeCareFiltersBar";
import { HomeCareProviderCard } from "@/components/home-care/HomeCareProviderCard";
import {
  useHomeCareProviders,
  defaultHomeCareFilters,
  type HomeCareFilters,
} from "@/hooks/use-home-care-providers";
import { Loader2, Home } from "lucide-react";

export default function HomeCarePage() {
  const [searchParams] = useSearchParams();
  const initialCounty = searchParams.get("county");
  const [filters, setFilters] = useState<HomeCareFilters>({
    ...defaultHomeCareFilters,
    county: initialCounty,
  });
  const { data: providers, isLoading } = useHomeCareProviders(filters);

  const currentYear = new Date().getFullYear();

  // Group by county for display
  const grouped = (providers || []).reduce<Record<string, any[]>>((acc, p) => {
    const key = p.county_name;
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  const countyNames = Object.keys(grouped).sort();

  return (
    <Layout>
      <Helmet>
        <title>{`Home Care Providers in Ireland (${currentYear}) | Find & Compare – Grandpal`}</title>
        <meta
          name="description"
          content={`Compare ${providers?.length || "130"}+ home care providers across Ireland. Filter by county, services, HSE approval & ratings. Find the right home care for your loved one.`}
        />
        <link rel="canonical" href="https://grandpal.co/home-care" />
      </Helmet>

      {/* Hero */}
      <div className="bg-grandpal-beige py-12">
        <div className="container text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Home className="h-6 w-6 text-primary" />
            <span className="text-sm font-medium text-primary uppercase tracking-wide">
              Home Care Directory
            </span>
          </div>
          <h1 className="text-4xl font-bold mb-4 mt-2">
            Home Care Providers in Ireland ({currentYear})
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Browse and compare home care providers across every county. Filter by
            services, HSE approval, and ratings to find the right care at home.
          </p>
        </div>
      </div>

      {/* Filters + Results */}
      <div className="container py-8 space-y-6">
        <HomeCareFiltersBar filters={filters} onChange={setFilters} />

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : providers?.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <p className="text-lg font-medium">No providers match your filters</p>
            <p className="text-sm mt-1">Try broadening your search criteria</p>
          </div>
        ) : (
          <div className="space-y-8">
            <p className="text-sm text-muted-foreground">
              Showing {providers?.length} provider{providers?.length !== 1 ? "s" : ""}{" "}
              across {countyNames.length} count{countyNames.length !== 1 ? "ies" : "y"}
            </p>

            {countyNames.map((county) => (
              <section key={county}>
                <h2 className="text-xl font-semibold mb-3 text-foreground">
                  {county}{" "}
                  <span className="text-sm font-normal text-muted-foreground">
                    ({grouped[county].length})
                  </span>
                </h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {grouped[county].map((provider: any) => (
                    <HomeCareProviderCard key={provider.id} provider={provider} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
